import React, {useState} from "react";
import { TextField } from "formik-material-ui";
import {Redirect} from "react-router-dom";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import Container from "@material-ui/core/Container";
import Card from "@material-ui/core/Card";
import { Formik, Form, Field, FormikProps } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import {Checkbox, FormControlLabel, Grid, makeStyles, Typography, useMediaQuery} from "@material-ui/core";
import {ReactComponent as CheckboxBorder} from "../../assets/checkboxBorder.svg";
import {privacyPolicyURL, termsOfUseURL, privacyPolicyAtlantaURl} from "../../constants/api.constants";
import {useTheme} from "@material-ui/core/styles";
import {useDispatch} from "react-redux";
import {authService} from "../../services"
import API from "../../helpers/apiHelpers";
import {signUpUser} from "../../redux/user/user.actions";
import ConfirmEmail from "../../components/ConfirmEmail/ConfirmEmail";

const useStyles = makeStyles({
    cardRoot: {
        width: "100%",
        padding: 40,
        marginBottom: 32,
    },
    containerRoot: {
        position: "relative",
        marginBottom: 55,
        zIndex: 1,
    },
});

interface Props {
    setIsOnBoarding: React.Dispatch<React.SetStateAction<boolean>>,
    setIsEmailConfirmed: () => void,
}

const SignUpPage = () => {
    const validationSchema = Yup.object().shape({
        firstName: Yup.string()
            .required("Required"),
        emailAddress: Yup.string()
            .email("Invalid email")
            .required("Required"),
        practiceCode: Yup.string()
            .required("Required"),
        password: Yup.string()
            .min(8, "Too Short!")
            .required("Required"),
        passwordConfirm: Yup.string()
            .oneOf([Yup.ref("password")], "Passwords must match")
            .required("Required"),
    });
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useDispatch();
    const theme = useTheme();
    const classes = useStyles();
    const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
    const primaryColor = "#273878";
    const [checkboxChecked, setCheckboxChecked] = useState(false);
    const [isEmailConfirmed, setIsEmailConfirmed] = useState(undefined);

    return (
        <>
        {(API.isLoggedIn && isEmailConfirmed) ? <Redirect to="/"/>
            :<Container className={classes.containerRoot} maxWidth="sm">
            <Card className={classes.cardRoot} style={{paddingBottom: "40px", marginTop: "80px"}}>
                {isEmailConfirmed === false
                    ?
                    <ConfirmEmail/>
                    :
                    <>
                        <Box marginTop="10px" marginBottom="8px">
                            <Typography
                                variant="h2"
                                align="center"
                            >
                                Get started
                            </Typography>
                        </Box>
                        <Formik
                            initialValues={{
                                firstName: "",
                                lastName: "",
                                emailAddress: "",
                                password: "",
                                passwordConfirm: "",
                                practiceCode: "",
                            }}
                            onSubmit={ async (values, {setStatus}) => {
                                try{
                                    const user = await dispatch(signUpUser({
                                        email: values.emailAddress.toLowerCase(),
                                        password: values.password,
                                        role: "VET",
                                        device: isMobile ? "MOBILE" : "WEB"
                                    }));
                                    API.token = user.authenticationToken.token;
                                    API.role = user.authenticationToken.role;
                                    API.userId = user.authenticationToken.userId;
                                    localStorage.setItem("profile", JSON.stringify({
                                        firstName: values.firstName,
                                        lastName: values.lastName,
                                        practiceCode: values.practiceCode,
                                    }));
                                    let expiry = new Date();
                                    expiry.setTime(expiry.getTime()+604800000);
                                    document.cookie = "isOnBoarding=true;path=/;expires="+expiry.toUTCString();
                                    // @ts-ignore
                                    setIsEmailConfirmed(false);
                                    setStatus({success: true});
                                } catch (error) {
                                    enqueueSnackbar((error.response.data.message || error.errorMessage), {
                                        variant: "error",
                                    });
                                    setStatus({success: false});
                                }
                            }}
                            validationSchema={validationSchema}
                        >
                            {
                                (props: FormikProps<{
                                    firstName: string,
                                    lastName: string,
                                    emailAddress: string,
                                    password: string,
                                    passwordConfirm: string,
                                    practiceCode: string
                                }>) => (
                                    <Form>
                                        <Box
                                            display="flex"
                                            flexDirection="column"
                                            flexWrap="wrap"
                                            width="100%"
                                            marginBottom="16px"
                                        >
                                            <Field
                                                component={TextField}
                                                name="firstName"
                                                label="First Name"
                                                variant="outlined"
                                                color="secondary"
                                                type="text"
                                                margin="normal"
                                                onChange={props.handleChange}
                                                value={props.values.firstName}
                                            />
                                            <Field
                                                component={TextField}
                                                name="lastName"
                                                label="Last Name"
                                                variant="outlined"
                                                color="secondary"
                                                type="text"
                                                margin="normal"
                                                onChange={props.handleChange}
                                                value={props.values.lastName}
                                            />
                                            <Field
                                                component={TextField}
                                                name="emailAddress"
                                                label="Email Address"
                                                variant="outlined"
                                                color="secondary"
                                                type="email"
                                                margin="normal"
                                                onChange={props.handleChange}
                                                value={props.values.emailAddress}
                                            />
                                            <Field
                                                component={TextField}
                                                name="practiceCode"
                                                label="Practice Code"
                                                variant="outlined"
                                                color="secondary"
                                                type="text"
                                                margin="normal"
                                                onChange={props.handleChange}
                                                value={props.values.practiceCode}
                                            />
                                            <Field
                                                component={TextField}
                                                label="Password"
                                                name="password"
                                                variant="outlined"
                                                color="secondary"
                                                type="password"
                                                margin="normal"
                                                onChange={props.handleChange}
                                                value={props.values.password}
                                            />
                                            <Field
                                                component={TextField}
                                                label="Confirm Password"
                                                name="passwordConfirm"
                                                variant="outlined"
                                                color="secondary"
                                                type="password"
                                                margin="normal"
                                                onChange={props.handleChange}
                                                value={props.values.passwordConfirm}
                                            />
                                        </Box>
                                        <Box display="flex" justifyContent="flex-start" alignItems="center" marginBottom="15px">
                                            <Checkbox
                                                color="secondary"
                                                icon={<CheckboxBorder />}
                                                style={{paddingLeft: 0}}
                                                value={checkboxChecked}
                                                onChange={() => {setCheckboxChecked(!checkboxChecked);}}
                                            />
                                            <Box>
                                                <Typography color="textPrimary" style={{fontSize: 14}}>
                                                    I agree to the&nbsp;
                                                    <a href={termsOfUseURL} target="_blank" rel="noreferrer" style={{color: primaryColor}}>Terms of Service</a>
                                                    &nbsp;and&nbsp;
                                                    <a href={privacyPolicyURL} target="_blank" rel="noreferrer" style={{color: primaryColor}}>Privacy Policy</a>
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box onClick={() => {
                                            if(!checkboxChecked) {
                                                // enqueueSnackbar("Please agree to our Terms Of Service and Privacy Policy first.",
                                                //     {
                                                //         variant: "error",
                                                //         autoHideDuration: 4000
                                                //     });
                                            }
                                        }}>
                                            <Button
                                                color="primary"
                                                variant="contained"
                                                type="submit"
                                                data-testid="submitButton"
                                                fullWidth
                                                disabled={!checkboxChecked}
                                            >
                                                Continue
                                            </Button>
                                        </Box>
                                    </Form>
                                )
                            }
                        </Formik>
                    </>}
            </Card>
        </Container>}
        </>
    );
};
export {SignUpPage}
